/** Batch dialog for the two mapping flags.
 *
 * Both are rendered as what the flag DOES rather than the column it lives in:
 *
 *   product matching  (use_for_matching) — take part in booking → tour matching
 *   vendor lookups    (api_queryable)    — may be sent to the vendor API
 *
 * The first one is why this exists. It was False on 0 of 9013 production rows
 * while 93 rows were parked by renaming the ID to `OFF/…` — the flag was not
 * unwanted, it was unreachable from where ops works.
 */
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

export type FlagKind = 'matching' | 'queryable';

const COPY: Record<
  FlagKind,
  { action: string; title: string; on: string; off: string; onHelp: string; offHelp: string }
> = {
  matching: {
    action: 'set_matching',
    title: 'Product matching',
    on: 'Use for matching',
    off: 'Do not use for matching',
    onHelp: 'Bookings carrying this external ID will resolve to the mapped tour.',
    offHelp:
      'The mapping is parked: bookings stop resolving through it, but the row and its tags are kept.',
  },
  queryable: {
    action: 'set_queryable',
    title: 'Vendor lookups',
    on: 'Allow vendor lookups',
    off: 'Block vendor lookups',
    onHelp: 'The ID may be sent to the restech API as a query argument.',
    offHelp:
      'For matching-only keys — option IDs and compound forms like “12345/14:00”, which the vendor does not recognise.',
  },
};

interface Props {
  ctx: BatchActionContext;
  kind: FlagKind;
}

export function SetFlagBatchDialog({ ctx, kind }: Props) {
  const copy = COPY[kind];
  const [value, setValue] = useState<'on' | 'off'>('off');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await ctx.runStandard({ value: value === 'on' });
      ctx.close();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="xs">
      <DialogTitle>{copy.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Applies to <strong>{ctx.matchedPhrase}</strong>.
          </Typography>
          <RadioGroup value={value} onChange={(e) => setValue(e.target.value as 'on' | 'off')}>
            <FormControlLabel value="on" control={<Radio />} label={copy.on} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, mb: 1 }}>
              {copy.onHelp}
            </Typography>
            <FormControlLabel value="off" control={<Radio />} label={copy.off} />
            <Typography variant="caption" color="text.secondary" sx={{ ml: 4 }}>
              {copy.offHelp}
            </Typography>
          </RadioGroup>
          <Alert severity="info">
            Only additional mappings carry these flags. A tour&apos;s own external ID is always
            used for matching, and those rows are reported as skipped.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={busy}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
